import { useState, useEffect, useRef, useCallback } from "react";
import { isTokenAboutToExpire, refreshTokenAutomatically } from "../shared/services/shared";
import { useUserActivity } from "./useUserActivity";
import { LOGOUT_REASONS } from "../constants/logoutReasons";

const FINAL_WINDOW_MS = 5 * 60 * 1000; // last 5 minutes window
const CHECK_INTERVAL_MS = 30000; // Check every 30 seconds
const ACTIVITY_THROTTLE_MS = 10000; // Check every 10 seconds on activity

export const useSessionExpiry = ({ isLoggedIn, logoutUser }) => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const checkIntervalRef = useRef(null);
  const modalShownRef = useRef(false);
  const dismissedRef = useRef(false); // Dismissed before final window
  const dismissedInFinalWindowRef = useRef(false); // Dismissed in final window

  // Check if modal should be shown
  const shouldShowModal = useCallback(() => {
    if (!isLoggedIn) return false;

    const { isExpiring, timeRemaining: remaining } = isTokenAboutToExpire();
    if (!isExpiring) {
      // Reset all flags if token is not expiring
      modalShownRef.current = false;
      dismissedRef.current = false;
      dismissedInFinalWindowRef.current = false;
      return false;
    }

    const withinFinalWindow = remaining <= FINAL_WINDOW_MS;

    // If dismissed in final window, never show again
    if (dismissedInFinalWindowRef.current) {
      return false;
    }

    // If dismissed before final window, only show in final window
    if (dismissedRef.current && !withinFinalWindow) {
      return false;
    }

    return true;
  }, [isLoggedIn]);

  // Handle user activity - check token expiry when user is active
  const handleUserActivity = useCallback(() => {
    if (!shouldShowModal()) {
      if (modalShownRef.current) {
        setShowSessionModal(false);
        modalShownRef.current = false;
      }
      return;
    }

    const { timeRemaining: remaining } = isTokenAboutToExpire();

    if (!modalShownRef.current) {
      setTimeRemaining(remaining);
      setShowSessionModal(true);
      modalShownRef.current = true;
    }
  }, [shouldShowModal]);

  // Track user activity
  useUserActivity(handleUserActivity, {
    throttleMs: ACTIVITY_THROTTLE_MS,
    enabled: isLoggedIn,
  });

  // Periodic check for token expiry (even without activity)
  useEffect(() => {
    if (!isLoggedIn) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      modalShownRef.current = false;
      setShowSessionModal(false);
      dismissedRef.current = false;
      dismissedInFinalWindowRef.current = false;
      return;
    }

    // Check token expiry every 30 seconds
    checkIntervalRef.current = setInterval(() => {
      if (!shouldShowModal()) {
        if (modalShownRef.current) {
          setShowSessionModal(false);
          modalShownRef.current = false;
        }
        return;
      }

      const { timeRemaining: remaining } = isTokenAboutToExpire();

      if (!modalShownRef.current) {
        setTimeRemaining(remaining);
        setShowSessionModal(true);
        modalShownRef.current = true;
      } else if (remaining > 0) {
        // Update remaining time if modal is already shown
        setTimeRemaining(remaining);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isLoggedIn, shouldShowModal]);

  // Update time remaining in modal and handle expiry
  useEffect(() => {
    if (!showSessionModal) return;

    const updateInterval = setInterval(() => {
      const { isExpiring, timeRemaining: remaining } = isTokenAboutToExpire();

      if (isExpiring && remaining > 0) {
        setTimeRemaining(remaining);
      } else if (remaining <= 0) {
        // Token expired, force logout
        setShowSessionModal(false);
        modalShownRef.current = false;
        dismissedRef.current = false;
        dismissedInFinalWindowRef.current = false;
        logoutUser(LOGOUT_REASONS.TOKEN_EXPIRED_AUTOMATIC_AFTER_PROMPT);
      } else {
        // Token no longer expiring (was refreshed)
        setShowSessionModal(false);
        modalShownRef.current = false;
        dismissedRef.current = false;
        dismissedInFinalWindowRef.current = false;
      }
    }, 1000);

    return () => clearInterval(updateInterval);
  }, [showSessionModal, logoutUser]);

  // Handle "Stay Signed In" button
  const handleStaySignedIn = useCallback(async () => {
    try {
      let isRefreshed = await refreshTokenAutomatically(true); // Log session extension
      if (!isRefreshed) {
        logoutUser(LOGOUT_REASONS.TOKEN_EXPIRED);
      }
      setShowSessionModal(false);
      modalShownRef.current = false;
      dismissedRef.current = false;
      dismissedInFinalWindowRef.current = false;
    } catch (error) {
      // If refresh fails, logout user
      logoutUser(LOGOUT_REASONS.TOKEN_EXPIRED);
    }
  }, [logoutUser]);

  // Handle "Log Out" button
  const handleLogoutClick = useCallback(() => {
    setShowSessionModal(false);
    modalShownRef.current = false;
    dismissedRef.current = false;
    dismissedInFinalWindowRef.current = false;
    logoutUser(LOGOUT_REASONS.SESSION_EXPIRY_MODAL_LOGOUT);
  }, [logoutUser]);

  // Handle "Not Now" button
  const handleNotNow = useCallback(() => {
    const { timeRemaining: remaining } = isTokenAboutToExpire();
    const withinFinalWindow = remaining <= FINAL_WINDOW_MS;

    setShowSessionModal(false);
    modalShownRef.current = false;

    if (withinFinalWindow) {
      // If in final window, mark as dismissed in final window - don't show again
      dismissedInFinalWindowRef.current = true;
    } else {
      // Remember dismissal until final 5-minute window
      dismissedRef.current = true;
    }
  }, []);

  return {
    showSessionModal,
    timeRemaining,
    handleStaySignedIn,
    handleLogoutClick,
    handleNotNow,
  };
};
